import type { AssessmentAnswerDto, EnglishTestQuestionDto } from '@/api/generated/models'
import type { EnglishTestAnswerReview, QuestionAnswerValue } from '../types'
import { byDisplayOrder, isPlainTextEntryQuestion, isTextEntryQuestion } from '../questionUtils'
import { buildAnswerReview, getCorrectAnswerTexts, getSortedCorrectAnswers, normalizeAnswerText } from './shared'

export function getInitialAnswerValue(
  question: EnglishTestQuestionDto,
  initialAnswer: AssessmentAnswerDto | undefined,
  storedEmailValue: string
): QuestionAnswerValue {
  const textAnswer = initialAnswer?.textAnswer ?? initialAnswer?.selectedAnswerText ?? ''

  if (isTextEntryQuestion(question)) {
    if (textAnswer.trim()) {
      return { type: 'text', value: textAnswer }
    }

    if (isPlainTextEntryQuestion(question) && storedEmailValue) {
      return { type: 'text', value: storedEmailValue }
    }

    return null
  }

  if (typeof initialAnswer?.selectedAnswerId === 'number') {
    const optionIndex = [...(question.answers ?? [])]
      .sort(byDisplayOrder)
      .findIndex(answer => answer.id === initialAnswer.selectedAnswerId)

    if (optionIndex >= 0) {
      return { type: 'option', optionIndex }
    }
  }

  return null
}

export function buildInitialSubmittedAnswer(
  question: EnglishTestQuestionDto,
  initialAnswer: AssessmentAnswerDto
): EnglishTestAnswerReview {
  const correctAnswers = getSortedCorrectAnswers(question)
  const selectedAnswerText = initialAnswer.textAnswer ?? initialAnswer.selectedAnswerText ?? ''
  const normalizedSelectedAnswer = normalizeAnswerText(selectedAnswerText)
  const locallyCorrect =
    correctAnswers.length > 0
      ? correctAnswers.some(
          answer => normalizeAnswerText(answer.answerText) === normalizedSelectedAnswer
        )
      : null

  return buildAnswerReview(question, {
    selectedAnswerId: initialAnswer.selectedAnswerId ?? null,
    selectedAnswerText,
    correctAnswerTexts: getCorrectAnswerTexts(question),
    locallyCorrect,
    backendCorrect: initialAnswer.correct ?? null,
    pointsEarned: initialAnswer.pointsEarned ?? null
  })
}

export function buildInitialAssessmentState(
  questions: EnglishTestQuestionDto[],
  initialAnswers: AssessmentAnswerDto[],
  storedEmailValue: string
) {
  const initialAnswersByQuestionId = new Map(
    initialAnswers
      .filter(answer => typeof answer.questionId === 'number')
      .map(answer => [answer.questionId!, answer])
  )
  const submittedTextAnswers: Record<number, string> = {}
  const answers = questions.map(question =>
    getInitialAnswerValue(
      question,
      question.id ? initialAnswersByQuestionId.get(question.id) : undefined,
      storedEmailValue
    )
  )
  const submittedAnswers = questions.reduce<Record<number, EnglishTestAnswerReview>>(
    (current, question) => {
      if (!question.id) {
        return current
      }

      const initialAnswer = initialAnswersByQuestionId.get(question.id)

      if (!initialAnswer) {
        return current
      }

      current[question.id] = buildInitialSubmittedAnswer(question, initialAnswer)
      const textAnswer = initialAnswer.textAnswer ?? initialAnswer.selectedAnswerText ?? ''
      if (isTextEntryQuestion(question) && textAnswer.trim()) {
        submittedTextAnswers[question.id] = textAnswer.trim()
      }
      return current
    },
    {}
  )

  return {
    answers,
    submittedAnswers,
    submittedTextAnswers
  }
}
